import { Navigate, useNavigate, useParams } from 'react-router'
import { isIosApp } from '@/lib/platform'
import { useTranslation } from 'react-i18next'
import { Alert, Badge, Button, Card, EmptyState, Skeleton } from '@/components/ui'
import { Breadcrumbs, PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter, useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import { useSubscriptionInvoice } from '@/hooks/useSubscription'
import type { SubscriptionInvoiceStatus } from '@/hooks/useSubscription'
import { useSubscriptionItemLabel } from './itemLabel'
import { useBillingPeriodLabel } from './periodLabel'

const SUBSCRIPTION_TAB_PATH = '/settings?tab=subscription'

function statusVariant(status: SubscriptionInvoiceStatus) {
  if (status === 'paid') return 'success' as const
  if (status === 'pending') return 'warning' as const
  return 'ghost' as const
}

/**
 * EP-144 (fix round B-1): one platform invoice, built the way /invoices/:id
 * is: breadcrumbs, a PageHeader carrying the number and the status badge, the
 * engine lines in a main card and the context in side cards.
 *
 * The list row only carries number, period, rooms, total, due and status; the
 * frozen engine lines and the payment block live on the :detail view alone, so
 * without this page an owner can see WHAT they were billed but never HOW it
 * was computed or when it was settled.
 *
 * Owner-only, the same gate as the Settings tab (Q-9): the route already sits
 * behind AdminRoute, which blocks staff and accountant, and an ADMIN who
 * follows a shared link lands back on Settings rather than on a 403 body.
 *
 * No arithmetic anywhere: quantity, unit_price and amount are three separate
 * server figures printed side by side, and the totals are the server's own.
 */
export function SubscriptionInvoiceDetailPage() {
  const { t } = useTranslation('settings')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const formatDate = useDateFormatter()
  const formatDateTime = useDateTimeFormatter()
  const itemLabel = useSubscriptionItemLabel()
  const periodLabel = useBillingPeriodLabel()

  const invoiceId = Number(id)
  // EP-150: not reachable in the iOS app, like the tab it belongs to.
  const isOwner = user?.role === 'owner' && !isIosApp()
  const { data: invoice, isLoading, isFetching, isError } = useSubscriptionInvoice(
    isOwner && Number.isFinite(invoiceId) && invoiceId > 0 ? invoiceId : null,
  )

  if (!isOwner) {
    return <Navigate to="/settings" replace />
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <Skeleton className="h-48" />
        </Card>
      </div>
    )
  }

  if (isError || !invoice) {
    return (
      <div className="space-y-6">
        <Alert variant="error" role="alert">
          {t('subscription.errors.load_failed')}
        </Alert>
        <Card>
          <EmptyState
            title={t('subscription.invoice_detail.not_found')}
            action={
              <Button onClick={() => navigate(SUBSCRIPTION_TAB_PATH)}>
                {t('subscription.invoice_detail.back')}
              </Button>
            }
          />
        </Card>
      </div>
    )
  }

  const payment = invoice.payment

  const facts: Array<{ key: string; label: string; value: string }> = [
    {
      key: 'period',
      label: t('subscription.invoices.columns.period'),
      value: periodLabel(invoice.period_from),
    },
    {
      key: 'period_range',
      label: t('subscription.invoice_detail.period_range'),
      value: `${formatDate(invoice.period_from)} - ${formatDate(invoice.period_to)}`,
    },
    {
      key: 'tier',
      label: t('subscription.tier_label'),
      value: t(`subscription.tiers.${invoice.tier}`),
    },
    {
      key: 'room_count',
      label: t('subscription.room_count_label'),
      value: String(invoice.room_count),
    },
    {
      key: 'due_at',
      label: t('subscription.invoices.columns.due_at'),
      value: formatDate(invoice.due_at),
    },
    {
      key: 'emailed_at',
      label: t('subscription.invoice_detail.emailed_at'),
      value: invoice.emailed_at
        ? formatDateTime(invoice.emailed_at)
        : t('subscription.invoice_detail.not_emailed'),
    },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('title'), href: '/settings' },
          { label: t('subscription.tab_label'), href: SUBSCRIPTION_TAB_PATH },
          { label: invoice.number },
        ]}
      />

      <PageHeader
        title={invoice.number}
        description={periodLabel(invoice.period_from)}
        isRefreshing={isFetching && !isLoading}
        // The status is the only thing the header carries: a platform invoice
        // is settled by a super admin through the admin plane (Q-5), so the
        // tenant surface is read-only and has no action to promote.
        actions={
          <Badge variant={statusVariant(invoice.status)}>
            {t(`subscription.invoices.status.${invoice.status}`)}
          </Badge>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* min-w-0: without it this grid child inflates to the items table's
            intrinsic width and overflows the viewport on phones. */}
        <Card className="lg:col-span-2 min-w-0">
          <h2 className="text-lg font-semibold mb-4">
            {t('subscription.invoice_detail.items')}
          </h2>

          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('subscription.invoice_detail.columns.description')}</th>
                  <th className="text-right">{t('subscription.invoice_detail.columns.quantity')}</th>
                  <th className="text-right">{t('subscription.invoice_detail.columns.unit_price')}</th>
                  <th className="text-right">{t('subscription.invoice_detail.columns.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={`${item.description_key}-${index}`}>
                    <td className="min-w-0">{itemLabel(item)}</td>
                    <td className="text-right tabular-nums">{item.quantity}</td>
                    <td className="text-right tabular-nums">{formatBaht(item.unit_price)}</td>
                    <td className="text-right font-medium tabular-nums">{formatBaht(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="text-right text-sm text-base-content/70">
                    {t('subscription.invoice_detail.subtotal')}
                  </td>
                  <td className="text-right text-sm tabular-nums">
                    {formatBaht(Number(invoice.subtotal))}
                  </td>
                </tr>
                <tr className="border-t-2">
                  <td colSpan={3} className="text-right font-semibold">
                    {t('subscription.next_invoice.total')}
                  </td>
                  <td className="text-right font-bold text-lg tabular-nums">
                    {formatBaht(Number(invoice.total))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        <div className="space-y-6 min-w-0">
          <Card title={t('subscription.invoice_detail.details')}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
              {facts.map((fact) => (
                <div key={fact.key} className="contents">
                  <dt className="min-w-0 text-sm text-base-content/60">{fact.label}</dt>
                  <dd className="min-w-0 text-sm font-medium text-right break-words">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card title={t('subscription.invoice_detail.payment')}>
            {payment ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                <dt className="min-w-0 text-sm text-base-content/60">
                  {t('subscription.invoice_detail.payment_amount')}
                </dt>
                <dd className="min-w-0 text-sm font-medium text-right tabular-nums">
                  {formatBaht(Number(payment.amount))}
                </dd>
                <dt className="min-w-0 text-sm text-base-content/60">
                  {t('subscription.invoice_detail.paid_at')}
                </dt>
                <dd className="min-w-0 text-sm font-medium text-right break-words">
                  {payment.paid_at ? formatDate(payment.paid_at) : '-'}
                </dd>
                <dt className="min-w-0 text-sm text-base-content/60">
                  {t('subscription.invoice_detail.payment_method')}
                </dt>
                <dd className="min-w-0 text-sm font-medium text-right break-words">
                  {payment.method
                    ? t(`subscription.invoice_detail.payment_methods.${payment.method}`)
                    : '-'}
                </dd>
              </dl>
            ) : (
              <p className="text-sm text-base-content/60">
                {t('subscription.invoice_detail.no_payment')}
              </p>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
